import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FAQItemProps {
  question: string;
  answer: string;
  value: string;
  openValue: string;
  onToggle: (v: string) => void;
}

const FAQItem = ({ question, answer, value, openValue, onToggle }: FAQItemProps) => {
  const isOpen = openValue === value;
  return (
    <div className="border-b border-white/10 last:border-0">
      <button
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 hover:bg-white/5 transition-colors duration-150"
        onClick={() => onToggle(isOpen ? '' : value)}
      >
        <span className="text-white/85 font-medium text-sm md:text-base leading-snug">{question}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          className="flex-shrink-0 text-[#c9a84c]"
        >
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 pt-1 text-white/60 text-sm md:text-base leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQPage = () => {
  const { t } = useTranslation();
  const [openValue, setOpenValue] = useState('');

  const faqData = [
    {
      label: t('faq.cat1_label'),
      accent: '#c9a84c',
      items: [
        { value: 'item-1', question: t('faq.q1'), answer: t('faq.a1') },
        { value: 'item-2', question: t('faq.q2'), answer: t('faq.a2') },
        { value: 'item-3', question: t('faq.q3'), answer: t('faq.a3') },
      ],
    },
    {
      label: t('faq.cat2_label'),
      accent: '#d97706',
      items: [
        { value: 'item-4', question: t('faq.q4'), answer: t('faq.a4') },
        { value: 'item-5', question: t('faq.q5'), answer: t('faq.a5') },
        { value: 'item-6', question: t('faq.q6'), answer: t('faq.a6') },
      ],
    },
    {
      label: t('faq.cat3_label'),
      accent: '#c9a84c',
      items: [
        { value: 'item-7', question: t('faq.q7'), answer: t('faq.a7') },
        { value: 'item-8', question: t('faq.q8'), answer: t('faq.a8') },
        { value: 'item-9', question: t('faq.q9'), answer: t('faq.a9') },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f1f3d] to-[#1a2f50] text-white">
      <Helmet>
        <title>{t('faq.page_title')}</title>
        <meta name="description" content={t('faq.meta_description')} />
      </Helmet>

      {/* ヘッダー */}
      <header className="px-4 md:px-8 py-5 flex items-center">
        <Link
          to="/"
          className="flex items-center gap-2 text-white/70 hover:text-[#c9a84c] transition-colors duration-200 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t('faq.back_home')}</span>
        </Link>
      </header>

      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="text-center px-4 pt-8 pb-12"
      >
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{t('faq.title')}</h1>
        <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-gradient-to-r from-[#c9a84c] to-[#d97706]" />
      </motion.section>

      {/* FAQ カテゴリー */}
      <main className="max-w-3xl mx-auto px-4 pb-20 space-y-10">
        {faqData.map((category, catIdx) => (
          <motion.div
            key={category.label}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: catIdx * 0.1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="h-5 w-1 rounded-full"
                style={{ backgroundColor: category.accent }}
              />
              <h2
                className="text-lg font-bold"
                style={{ color: category.accent }}
              >
                {category.label}
              </h2>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">
              {category.items.map((item) => (
                <FAQItem
                  key={item.value}
                  value={item.value}
                  question={item.question}
                  answer={item.answer}
                  openValue={openValue}
                  onToggle={setOpenValue}
                />
              ))}
            </div>
          </motion.div>
        ))}
      </main>

      {/* フッター */}
      <footer className="border-t border-white/10 py-8">
        <div className="container mx-auto px-4 text-center text-white/40 text-sm">
          <p>&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
          <p className="mt-1">{t('faq.footer_tagline')}</p>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
